import DesktopWindowsSharp from "@mui/icons-material/DesktopWindowsSharp";
import LibraryBooksSharp from "@mui/icons-material/LibraryBooksSharp";
import PictureAsPdfSharp from "@mui/icons-material/PictureAsPdfSharp";
import {
  GridColumn as Column,
  GridCellProps,
} from "@progress/kendo-react-grid";
import {
  DateCellContent,
  DateCellProps,
  DEFAULT_DATETIME_FORMAT,
  defaultGridProps,
  GridProvider,
  InputType,
  isNullEmptyOrUndefined,
  MainGrid,
  MultiModalProvider,
  PageCard,
  PageCardRow,
  PageCardSection,
  PrimaryButton,
  Render,
  SecondaryButton,
  SmartInputBlock,
  TextIconButton,
  TimeStampCell,
  useFormHelpers,
  useFormState,
  useGridState,
  useLocalState,
  useLocalStorage,
  useToasts,
} from "@techsharegov/react-ui";
import { LocalCollapsibleFormFiltersPageCard } from "Components/Form";
import { AgendaEndpoints } from "Models/Routes";
import { Routes } from "Navigation";
import { Fragment, useEffect, useRef } from "react";
import { Row } from "reactstrap";
import { useFileDownloader } from "Utilities";
import { UpcomingMeetingSearchModel } from "./Models";
import { Link } from "react-router-dom";

export type MeetingsGridProps = {
  committeeId?: string;
};

export type UpcomingMeetingLocalState = {
  isCardOpen: boolean;
  toggleIsCardOpen: () => void;
  searchValues: UpcomingMeetingSearchModel;
  setSearchValues: React.Dispatch<
    React.SetStateAction<UpcomingMeetingSearchModel | null>
  >;
  shouldClearGridData: boolean;
  setShouldClearGridData: React.Dispatch<React.SetStateAction<boolean>>;
};

export const CurrentAndUpcomingMeetings: React.FC<MeetingsGridProps> = ({
  committeeId = undefined,
}) => {
  return (
    <Fragment>
      <UpcomingMeetingSearchFields committeeId={committeeId} />
      <GridProvider entityName="Meeting">
        <CurrentAndUpcomingMeetingsGrid committeeId={committeeId} />
      </GridProvider>
    </Fragment>
  );
};

const UpcomingMeetingSearchFields: React.FC<MeetingsGridProps> = ({
  committeeId,
}) => {
  const [{ isCardOpen, toggleIsCardOpen }] =
    useLocalState<UpcomingMeetingLocalState>();

  const { updateCurrentSearch } = useDetailedSearch();

  return (
    <LocalCollapsibleFormFiltersPageCard
      collapsibleTitle="Search Parameters"
      isOpen={isCardOpen}
      toggleIsOpen={toggleIsCardOpen}
      filterChipsProps={{
        onClear: updateCurrentSearch,
      }}
    >
      <PageCardRow>
        <PageCardSection thin short>
          <Row>
            <SmartInputBlock
              name="description"
              label="Meeting Title"
              type={InputType.Text}
            />

            <SmartInputBlock
              name="meetingType"
              label="Meeting Type"
              type={InputType.DropDown}
              readEndpoint={
                committeeId === undefined
                  ? AgendaEndpoints.meetingtypes.dropdownItems
                  : AgendaEndpoints.meetingtypes.dropdownItems +
                    "?committeeId=" +
                    committeeId
              }
              className="no-submit-enter"
            />
            <SmartInputBlock
              name="startDateFrom"
              label="Meeting Date From"
              type={InputType.DatePicker}
            />
            <SmartInputBlock
              name="startDateTo"
              label="Meeting Date To"
              type={InputType.DatePicker}
            />
            <SmartInputBlock
              name="committeeId"
              label=""
              hidden
              type={InputType.Text}
            />
          </Row>
          <DetailedSearchButton />
        </PageCardSection>
      </PageCardRow>
    </LocalCollapsibleFormFiltersPageCard>
  );
};

export const CurrentAndUpcomingMeetingsGrid: React.FC<MeetingsGridProps> = ({
  committeeId,
}) => {
  const [{ searchValues, shouldClearGridData, setShouldClearGridData }] =
    useLocalState<UpcomingMeetingLocalState>();
  const { total, setData, setTotal } = useGridState();

  useEffect(() => {
    if (shouldClearGridData) {
      setData([]);
      setTotal(0);
      setShouldClearGridData(false);
    }
  }, [shouldClearGridData]);

  return (
    <PageCard title="Upcoming Meetings">
      <MultiModalProvider>
        <Render condition={committeeId === undefined}>
          <MainGrid
            readEndpoint={AgendaEndpoints.meetings.controllerActionString(
              "readCurrentAndUpcoming"
            )}
            search={searchValues}
            refreshOnSearchChange
            sortable={true}
            groupable={false}
            {...defaultGridProps}
            pageSize={25}
            pageable
            ariaLabel="Upcoming Meetings"
          >
            <Column field="description" title="Meeting Title" width="auto" />
            <Column field="meetingType" title="Meeting Type" width="auto" />
            <Column
              field="meetingStartDateTime"
              title="Date/Time"
              width="160px"
              cell={DateCell}
            />
            <Column
              field="id"
              title="Documents"
              cell={ActionsCell}
              width="auto"
            />
          </MainGrid>
        </Render>
        <Render condition={committeeId !== undefined}>
          <MainGrid
            readEndpoint={AgendaEndpoints.meetings.controllerActionString(
              "readCurrentAndUpcoming"
            )}
            search={{ ...searchValues, committeeId }}
            refreshOnSearchChange
            sortable={true}
            groupable={false}
            {...defaultGridProps}
            pageSize={50}
            pageable
            ariaLabel="Upcoming Meetings"
          >
            <Column field="description" title="Meeting Title" width="auto" />
            <Column
              field="meetingStartDateTime"
              title="Date/Time"
              width="160px"
              cell={DateCell}
            />
            <Column
              field="id"
              title="Documents"
              cell={ActionsCell}
              width="auto"
            />
          </MainGrid>
        </Render>
      </MultiModalProvider>
    </PageCard>
  );
};

const ActionsCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  const { refreshGrid } = useGridState();
  const toast = useToasts();
  const { downloadFile } = useFileDownloader();
  const dataItem = props.dataItem;

  const goToHtmlAgenda = async () => {
    window.location.href = `${Routes.HtmlAgenda}/${dataItem?.id}`;
  };

  const downloadAgenda = async () => {
    try {
      var url =
        AgendaEndpoints.meetingattachments.controllerActionString("download") +
        "?id=" +
        dataItem.agendaAttachmentId;

      downloadFile(url, "MeetingAgenda");
      refreshGrid();
    } catch {
      toast.error("Error downloading document.");
    }
  };

  const downloadMinutes = async () => {
    try {
      var url =
        AgendaEndpoints.meetingattachments.controllerActionString("download") +
        "?id=" +
        dataItem.minutesAttachmentId;

      downloadFile(url, "MeetingMinutes");
      refreshGrid();
    } catch {
      toast.error("Error downloading document.");
    }
  };

  return (
    <td
      aria-colindex={props.ariaColumnIndex}
      colSpan={props.colSpan}
      className={props.className}
      role="gridcell"
    >
      <Render condition={dataItem?.isPublished}>
        <Link
          aria-label={
            dataItem?.description +
            " " +
            dataItem?.meetingStartDateTimeDisplay +
            " " +
            "html agenda"
          }
          title={
            dataItem?.description +
            " " +
            dataItem?.meetingStartDateTimeDisplay +
            " " +
            "html agenda"
          }
          to={`${Routes.HtmlAgenda}/${dataItem?.id}`}
          target="blank"
        >
          <TextIconButton
            aria-hidden
            icon={DesktopWindowsSharp}
            className="single-line-btn"
          >
            Html Agenda
          </TextIconButton>
        </Link>
        <br />
      </Render>
      <Render condition={dataItem?.hasAgenda && dataItem?.isPublished}>
        <Link
          aria-label={
            dataItem?.description +
            " " +
            dataItem?.meetingStartDateTimeDisplay +
            " " +
            "download agenda"
          }
          title={
            dataItem?.description +
            " " +
            dataItem?.meetingStartDateTimeDisplay +
            " " +
            "download agenda"
          }
          to="#"
          onClick={downloadAgenda}
        >
          <TextIconButton
            name={
              dataItem?.description +
              " " +
              dataItem?.meetingStartDateTimeDisplay +
              " " +
              "download agenda"
            }
            icon={PictureAsPdfSharp}
            className="single-line-btn"
          >
            Agenda
          </TextIconButton>
        </Link>
        <br />
      </Render>
      <Render condition={dataItem?.hasMinutes}>
        <Link
          aria-label={
            dataItem?.description +
            " " +
            dataItem?.meetingStartDateTimeDisplay +
            " " +
            "download minutes"
          }
          title={
            dataItem?.description +
            " " +
            dataItem?.meetingStartDateTimeDisplay +
            " " +
            "download minutes"
          }
          to="#"
          onClick={downloadMinutes}
        >
          <TextIconButton
            name={
              dataItem?.description +
              " " +
              dataItem?.meetingStartDateTimeDisplay +
              " " +
              "download minutes"
            }
            icon={LibraryBooksSharp}
            className="single-line-btn"
            tabIndex={0}
          >
            Minutes
          </TextIconButton>
        </Link>
      </Render>
    </td>
  );
};

const DateCell: React.FC<DateCellProps> = (props) => {
  return (
    <td
      aria-colindex={props.ariaColumnIndex}
      colSpan={props.colSpan}
      className={props.className}
      role="gridcell"
    >
      <DateCellContent dateFormat={DEFAULT_DATETIME_FORMAT} {...props} />
    </td>
  );
};

const DetailedSearchButton: React.FC<MeetingsGridProps> = () => {
  const { formValues, runLastSearch, search, updateCurrentSearch } =
    useDetailedSearch();
  const { setFormValues } = useFormHelpers();

  const handleClearAll = (e: any) => {
    e.stopPropagation();
    setFormValues({});
    updateCurrentSearch();
  };

  return (
    <Fragment>
      <Row>
        <SecondaryButton
          className="mr-regular"
          onClick={handleClearAll}
          disabled={isNullEmptyOrUndefined(formValues)}
        >
          Clear all
        </SecondaryButton>
        <SecondaryButton className="mr-regular" onClick={runLastSearch}>
          Run Last Search
        </SecondaryButton>
        <PrimaryButton
          onClick={search}
          disabled={isNullEmptyOrUndefined(formValues)}
        >
          Search
        </PrimaryButton>
      </Row>
    </Fragment>
  );
};

export const useDetailedSearch = () => {
  const [{ isCardOpen, toggleIsCardOpen, setSearchValues }] =
    useLocalState<UpcomingMeetingLocalState>();

  const shouldUpdateSearch = useRef<boolean>(false);
  const { formValues } = useFormState();
  const { setFormValues } = useFormHelpers();
  const [{ setShouldClearGridData }] =
    useLocalState<UpcomingMeetingLocalState>();
  const [, setSearchModel, getPreviousSearchModel] =
    useLocalStorage<UpcomingMeetingSearchModel>(
      "searchModel",
      new UpcomingMeetingSearchModel()
    );

  const saveSearchFilters = () => {
    if (!isNullEmptyOrUndefined(formValues)) {
      setSearchModel(formValues);
    }
  };

  const runLastSearch = () => {
    setFormValues(getPreviousSearchModel());
    shouldUpdateSearch.current = true;
  };

  const updateCurrentSearch = () => {
    shouldUpdateSearch.current = true;
  };

  const search = () => {
    saveSearchFilters();
    setSearchValues(formValues);
    if (isNullEmptyOrUndefined(formValues)) {
      setShouldClearGridData(true);
    }
    if (!isNullEmptyOrUndefined(formValues) && isCardOpen) {
      toggleIsCardOpen();
    }
  };

  useEffect(() => {
    if (shouldUpdateSearch.current) {
      search();
      shouldUpdateSearch.current = false;
    }
  }, [formValues]);

  return { formValues, runLastSearch, search, updateCurrentSearch };
};
